import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MAP_TYPE_LABELS, type MapVersion } from '@course-authoring/shared-types';
import {
  useMapVersions,
  useDeleteMapVersion,
  useDuplicateMapVersion,
} from '../api/mapApi';
import { useRememberListPath } from '../hooks/useListPathMemory';
import {
  AdminIcon,
  AdminPageHeader,
  AdminStatusMessage,
  adminActionIcons,
  createAdminListStyles,
  useAdminNarrowViewport,
} from '../admin-ui';

export function MapListPage() {
  const navigate = useNavigate();
  useRememberListPath('/maps');
  const isNarrow = useAdminNarrowViewport();
  const styles = useMemo(() => createAdminListStyles({ narrow: isNarrow }), [isNarrow]);
  const deleteDialogTitleId = useId();
  const duplicateDialogTitleId = useId();

  const { data: mapVersions, isLoading } = useMapVersions();
  const [searchParams, setSearchParams] = useSearchParams();
  // See useListControls.ts: setSearchParams' identity changes with the URL, so
  // route calls through a ref to keep setSearchQuery itself stable.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const searchQuery = searchParams.get('q') ?? '';
  const setSearchQuery = useCallback((value: string) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('q', value); else next.delete('q');
      return next;
    }, { replace: true });
  }, []);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [duplicateConfirmId, setDuplicateConfirmId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const displayedVersions = useMemo(() => {
    if (!mapVersions) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return mapVersions;
    return mapVersions.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        MAP_TYPE_LABELS[v.mapType].toLowerCase().includes(q)
    );
  }, [mapVersions, searchQuery]);

  const { mutate: doDuplicate, isPending: isDuplicating } = useDuplicateMapVersion({
    onSuccess: (data) => {
      setDuplicateConfirmId(null);
      navigate(`/maps/${data.id}`);
    },
    onError: (error) => {
      setActionError(error.message || 'Failed to duplicate map version');
      setDuplicateConfirmId(null);
    },
  });

  const { mutate: doDelete, isPending: isDeleting } = useDeleteMapVersion({
    onSuccess: () => {
      setDeleteConfirmId(null);
      setActionError(null);
    },
    onError: (error) => {
      setActionError(error.message || 'Failed to delete map version');
      setDeleteConfirmId(null);
    },
  });

  const handleCreate = useCallback(() => {
    navigate('/maps/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (id: string) => {
      navigate(`/maps/${id}`);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((id: string) => {
    setActionError(null);
    setDeleteConfirmId(id);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmId) {
      doDelete(deleteConfirmId);
    }
  }, [deleteConfirmId, doDelete]);

  const handleCancelDelete = useCallback(() => {
    if (isDeleting) return;
    setDeleteConfirmId(null);
  }, [isDeleting]);

  const versionToDelete = deleteConfirmId
    ? displayedVersions.find((v: MapVersion) => v.id === deleteConfirmId)
    : null;

  const versionToDuplicate = duplicateConfirmId
    ? displayedVersions.find((v: MapVersion) => v.id === duplicateConfirmId)
    : null;

  return (
    <>
      <div style={styles.page}>
        <AdminPageHeader
          title="Maps"
          description="Interactive maps with region descriptions, used by map blocks in lessons."
          actions={(
            <button type="button" onClick={handleCreate} style={styles.addLessonButton}>
              + New Map
            </button>
          )}
        />

        <div style={styles.listToolbar}>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search title or type..."
            style={styles.filterInput}
            aria-label="Search maps"
          />
          <span style={styles.resultsCount}>
            {displayedVersions.length} {displayedVersions.length === 1 ? 'result' : 'results'}
          </span>
          {searchQuery.trim() && (
            <button type="button" onClick={() => setSearchQuery('')} style={styles.resetFiltersButton}>
              Reset
            </button>
          )}
        </div>

        {actionError && (
          <AdminStatusMessage tone="error" role={null}>{actionError}</AdminStatusMessage>
        )}

        <section style={styles.panel}>
          {isLoading ? (
            <p style={styles.stateText}>Loading maps...</p>
          ) : displayedVersions.length === 0 ? (
            <p style={styles.stateText}>No maps yet. Create one to get started.</p>
          ) : (
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.headerCell}>Title</th>
                    <th style={styles.headerCell}>Map Type</th>
                    <th style={styles.headerCell}>Regions</th>
                    <th style={{ ...styles.headerCellActions, width: 'auto' }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {displayedVersions.map((v: MapVersion) => (
                    <tr key={v.id} style={styles.tableRow}>
                      <td style={styles.bodyCell}>
                        <button
                          type="button"
                          onClick={() => handleEdit(v.id)}
                          style={styles.titleButton}
                        >
                          {v.title || 'Untitled Map'}
                        </button>
                      </td>
                      <td style={styles.bodyCell}>{MAP_TYPE_LABELS[v.mapType]}</td>
                      <td style={styles.bodyCell}>{v.regions.length}</td>
                      <td style={{ ...styles.actionsCell, width: 'auto' }}>
                        <div style={styles.rowActions}>
                          <button
                            type="button"
                            onClick={() => { setActionError(null); setDuplicateConfirmId(v.id); }}
                            style={styles.rowActionButton}
                          >
                            Duplicate
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(v.id)}
                            style={{ ...styles.rowActionButton, ...styles.rowActionButtonDanger }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {deleteConfirmId && (
        <div style={styles.modalOverlay}>
          <div
            style={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={deleteDialogTitleId}
          >
            <h3 style={styles.modalTitle} id={deleteDialogTitleId}>Delete Map?</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete &quot;{versionToDelete?.title || 'Untitled Map'}&quot;?
              This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={handleCancelDelete}
                style={styles.cancelButton}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                style={styles.confirmDeleteButton}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {duplicateConfirmId && (
        <div style={styles.modalOverlay}>
          <div
            style={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={duplicateDialogTitleId}
          >
            <h3 style={styles.modalTitle} id={duplicateDialogTitleId}>Duplicate Map?</h3>
            <p style={styles.modalText}>
              Are you sure you want to duplicate &quot;{versionToDuplicate?.title || 'Untitled Map'}&quot;?
              A copy will be created with all regions and descriptions.
            </p>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => { if (!isDuplicating) setDuplicateConfirmId(null); }}
                style={styles.cancelButton}
                disabled={isDuplicating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { if (duplicateConfirmId) doDuplicate(duplicateConfirmId); }}
                style={styles.confirmActionButton}
                disabled={isDuplicating}
              >
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
